import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './HowItWorks.css';

const HowItWorks = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('seller');
  const [openFAQ, setOpenFAQ] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      q: 'How long does it take to list an animal on Maveshi Bids?',
      a: 'Most listings take under 15 minutes. You need clear photos, basic health information, breed details, and your reserve price. Our listing assistant walks you through each field step by step.'
    },
    {
      q: 'When does my auction go live after listing?',
      a: 'Listings are reviewed and approved within 24 hours. Public auctions are then assigned to the next available weekly auction slot. Private auctions can be scheduled at any date and time that suits you and your invited buyers.'
    },
    {
      q: 'What if my reserve price is not met?',
      a: 'If bidding does not reach your reserve price, the lot is passed in — meaning it remains unsold. You can relist at the next auction at no additional listing fee. This protects you from selling below your minimum acceptable price.'
    },
    {
      q: 'How does payment work for buyers and sellers?',
      a: 'When a buyer wins a lot, payment is secured through the Maveshi Bids platform. Funds are held safely until the seller confirms that delivery or collection has been completed. This protects both parties and removes the risk of non-payment or misrepresentation.'
    },
    {
      q: 'Can I bid if I am outside Pakistan?',
      a: 'Yes. International buyers, including Pakistani diaspora in the Gulf countries, UK, Europe, and North America, are welcome and actively participate in our auctions. You will need to complete our international buyer verification process which requires a valid passport and proof of address.'
    },
    {
      q: 'What happens if there is a dispute about a lot?',
      a: 'If a listing is found to be materially different from what was described, buyers can raise a dispute through the platform. Our team reviews the case using the auction\'s full audit trail and works to reach a fair resolution for both parties. Funds remain held until the dispute is resolved.'
    }
  ];

  const sellerSteps = [
    {
      number: 1,
      label: 'Step 1 — Seller',
      title: 'Register & Verify Your Account',
      description: 'Sign up for free at maveshibids.com. You will need your CNIC number, a valid phone number, and your province or district. Verification is quick and protects all sellers and buyers on the platform. Once verified, your seller dashboard is ready immediately.',
      tips: ['📱 Works on mobile', '🆓 Free to register', '✅ CNIC verification', '⚡ Approved within 24hrs']
    },
    {
      number: 2,
      label: 'Step 2 — Seller',
      title: 'Create Your Listing',
      description: 'Upload clear photos and a short video of your animal, horse, machine, or land. Add key details: breed, age, weight, health records, vaccination status, and any third-party assessment reports. The more information you provide, the more confident buyers will be — and the higher your bids.',
      tips: ['📸 Photos & Video', '📋 Health Details', '🏷️ Set Reserve Price', '🐄 Multi-category']
    },
    {
      number: 3,
      label: 'Step 3 — Seller',
      title: 'Choose Public or Private Auction',
      description: 'Select a Public Auction to reach every verified buyer on the platform — maximum competition, maximum price. Or choose a Private Auction to invite a curated set of pre-approved buyers for premium, stud, or sensitive lots. Your listing is then reviewed by our team and approved within 24 hours.',
      tips: ['🌍 Public — all buyers', '🔒 Private — invite only', '📅 Weekly slots', '🔔 Buyers notified']
    },
    {
      number: 4,
      label: 'Step 4 — Seller',
      title: 'Watch Buyers Compete Live',
      description: 'When your lot goes live, registered buyers from across Pakistan and internationally place real-time bids. You can watch the auction unfold in your dashboard. The bidding system is fully transparent — all bids are logged, timestamped, and visible. No one gets priority treatment.',
      tips: ['📊 Live dashboard', '🔔 Bid alerts', '🌐 National + international', '⚖️ 100% fair']
    },
    {
      number: 5,
      label: 'Step 5 — Seller',
      title: 'Auction Closes. Get Paid.',
      description: 'When the auction closes, the highest bidder wins the lot. Payment is secured through the platform. Once you confirm delivery or collection with the buyer, funds are released to your account. Full digital records are maintained for every transaction — no disputes, no confusion.',
      tips: ['💰 Secure payment', '📦 Delivery coordination', '🧾 Digital records', '🤝 Dispute protection']
    }
  ];

  const buyerSteps = [
    {
      number: 1,
      label: 'Step 1 — Buyer',
      title: 'Register & Get Verified',
      description: 'Create your free buyer account with your CNIC and phone number. Buyers from Pakistan and abroad (including the Gulf, UK, and beyond) can register. Verification ensures a trusted marketplace — every person you bid against is real and accountable.',
      tips: ['🆓 Free to register', '🌍 International buyers welcome', '✅ CNIC or Passport']
    },
    {
      number: 2,
      label: 'Step 2 — Buyer',
      title: 'Browse Upcoming Auctions',
      description: 'Explore all upcoming lots — livestock, horses, machinery, land — with full details, photos, videos, and health records. Filter by breed, location, age, weight, or category. Save lots to your watchlist and get notified when they go live.',
      tips: ['🔍 Advanced filters', '❤️ Watchlist', '🔔 Auction reminders']
    },
    {
      number: 3,
      label: 'Step 3 — Buyer',
      title: 'Deposit & Set Your Limit',
      description: 'Before bidding, place a refundable security deposit. Set your maximum bid limit — the system will automatically bid on your behalf up to that limit so you never miss a lot even if you\'re offline. Unused deposits are fully refunded.',
      tips: ['💳 Refundable deposit', '🤖 Auto-bidding available', '🔒 Secured funds']
    },
    {
      number: 4,
      label: 'Step 4 — Buyer',
      title: 'Bid Live — Equal Chance for Everyone',
      description: 'When the auction starts, place your bids in real time. Every buyer — whether from Lahore, Karachi, Peshawar, or Dubai — starts on equal footing. No waiting lists, no preference for family or friends of the seller, no inside deals. Highest bid wins. Always.',
      tips: ['⚖️ Zero favouritism', '📱 Bid from your phone', '⚡ Real-time updates']
    },
    {
      number: 5,
      label: 'Step 5 — Buyer',
      title: 'Win the Lot. Arrange Collection.',
      description: 'If your bid is highest at close, you win the lot. Coordinate with the seller for delivery or collection. Payment is held securely until both parties confirm the transaction. You are protected every step of the way.',
      tips: ['🏆 Instant confirmation', '📞 Seller contact', '🛡️ Buyer protection']
    }
  ];

  const JourneyStep = ({ step }) => (
    <div className="journey-step">
      <div className="step-left">
        <div className="step-circle">{step.number}</div>
        <div className="step-line"></div>
      </div>
      <div className="step-right">
        <div className="step-label">{step.label}</div>
        <h3>{step.title}</h3>
        <p>{step.description}</p>
        <div className="step-tips">
          {step.tips.map((tip, idx) => (
            <span key={idx} className="tip-pill">{tip}</span>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="how-it-works-page">
      {/* Hero Section */}
      <header className="page-hero">
        <div className="page-hero-inner">
          <div className="hero-tag">Simple. Fair. Transparent.</div>
          <h1>How <span>Maveshi Bids</span> Works</h1>
          <p>From listing your first animal to receiving payment — here is everything you need to know about buying and selling on Pakistan's first online live auction platform.</p>
        </div>
      </header>

      {/* Journey Steps */}
      <section className="journey-section">
        <div className="section-inner">
          <div>
            <span className="section-tag">Step-by-Step Process</span>
            <h2 className="section-title">Your Complete Journey on Maveshi Bids</h2>
            <p className="section-sub">Whether you are selling a prized Sahiwal bull or bidding on a Thoroughbred mare, the process is the same — straightforward, digital, and fair.</p>
          </div>

          <div className="tab-panels">
            <div className={`tab-panel ${activeTab === 'seller' ? 'active' : ''}`}>
              <div className="journey-steps">
                {sellerSteps.map((step) => (
                  <JourneyStep key={step.number} step={step} />
                ))}
              </div>
            </div>

            <div className={`tab-panel ${activeTab === 'buyer' ? 'active' : ''}`}>
              <div className="journey-steps">
                {buyerSteps.map((step) => (
                  <JourneyStep key={step.number} step={step} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Roles Section */}
      <section className="roles-section">
        <div className="section-inner">
          <div style={{ textAlign: 'center' }}>
            <span className="section-tag">For Sellers & Buyers</span>
            <h2 className="section-title" style={{ textAlign: 'center' }}>Built for Everyone in Pakistan's Agricultural Market</h2>
          </div>

          <div className="roles-grid">
            <div className="role-card">
              <div className="role-header">
                <div className="role-icon">🧑‍🌾</div>
                <h3>For Sellers</h3>
                <p>From small farmers with one animal to large breeders with full herds — Maveshi Bids gives every seller access to the widest possible buyer pool.</p>
              </div>
              <div className="role-steps">
                <div className="role-step">
                  <div className="rs-num">✓</div>
                  <p><strong>Get true market value</strong> — not a price negotiated with a single buyer in a mandi</p>
                </div>
                <div className="role-step">
                  <div className="rs-num">✓</div>
                  <p><strong>Sell from anywhere</strong> — no transport, no travel, no weather disruptions</p>
                </div>
                <div className="role-step">
                  <div className="rs-num">✓</div>
                  <p><strong>Public or private auctions</strong> — choose who can bid on your lot</p>
                </div>
                <div className="role-step">
                  <div className="rs-num">✓</div>
                  <p><strong>No middlemen commission</strong> — you deal directly with the winning buyer</p>
                </div>
                <div className="role-step">
                  <div className="rs-num">✓</div>
                  <p><strong>Reach international buyers</strong> — Pakistani diaspora and overseas buyers participate regularly</p>
                </div>
              </div>
            </div>

            <div className="role-card">
              <div className="role-header">
                <div className="role-icon">🛒</div>
                <h3>For Buyers</h3>
                <p>Whether you are a breeder, trader, farmer, or investor, Maveshi Bids gives you access to quality-verified lots from across all of Pakistan.</p>
              </div>
              <div className="role-steps">
                <div className="role-step">
                  <div className="rs-num">✓</div>
                  <p><strong>Equal chance as every other buyer</strong> — no waiting lists, no friend-of-the-seller priority</p>
                </div>
                <div className="role-step">
                  <div className="rs-num">✓</div>
                  <p><strong>Verified listings with photos, videos, health records</strong> — know exactly what you are buying</p>
                </div>
                <div className="role-step">
                  <div className="rs-num">✓</div>
                  <p><strong>Bid from anywhere in Pakistan or abroad</strong> — on any device, any time</p>
                </div>
                <div className="role-step">
                  <div className="rs-num">✓</div>
                  <p><strong>Auto-bidding</strong> — set your max limit and the system bids for you</p>
                </div>
                <div className="role-step">
                  <div className="rs-num">✓</div>
                  <p><strong>Full buyer protection</strong> — payment held securely until delivery is confirmed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="trust-section">
        <div className="section-inner">
          <div style={{ textAlign: 'center' }}>
            <span className="section-tag">Why Pakistan Trusts Us</span>
            <h2 className="section-title" style={{ textAlign: 'center' }}>Built on Transparency</h2>
          </div>

          <div className="trust-grid">
            <div className="trust-card">
              <div className="emoji">🔐</div>
              <h4>Verified Participants</h4>
              <p>Every buyer and seller is CNIC-verified before they can participate in any auction.</p>
            </div>
            <div className="trust-card">
              <div className="emoji">📜</div>
              <h4>Full Audit Trail</h4>
              <p>Every bid, every message, every transaction is logged with timestamps and cannot be altered.</p>
            </div>
            <div className="trust-card">
              <div className="emoji">🛡️</div>
              <h4>Buyer & Seller Protection</h4>
              <p>Payments are held in escrow until both parties confirm the transaction is complete.</p>
            </div>
            <div className="trust-card">
              <div className="emoji">📞</div>
              <h4>Dedicated Support</h4>
              <p>Our team is available via WhatsApp, phone, and email to assist at every step of your auction.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="section-inner">
          <div>
            <span className="section-tag">Common Questions</span>
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-sub">Quick answers about the bidding process for sellers and buyers on Maveshi Bids.</p>
          </div>

          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-item ${openFAQ === index ? 'open' : ''}`}
              >
                <div
                  className="faq-q"
                  tabIndex="0"
                  onClick={() => toggleFAQ(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleFAQ(index);
                    }
                  }}
                  role="button"
                >
                  {faq.q}
                  <div className="faq-arrow">▾</div>
                </div>
                <p className="faq-a">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-strip">
        <div>
          <h2>Ready to Start Your First Auction?</h2>
          <p>Registration is free. Your first listing is free. Join Pakistan's fastest growing agricultural auction community today.</p>
          <div className="cta-btns">
            <button className="btn-primary" onClick={() => navigate('/register')}>Register Free →</button>
            <button className="btn-outline" onClick={() => navigate('/pricing')}>View Pricing</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
